import { useState } from 'react';
import PropTypes from 'prop-types';

import { login } from '../../../../api/auth';
import { setCookieByName } from '../../../../utils';
import { getActiveBranch, getUser } from '../../../../api/users';

/**
 * Custom hook for handling user login.
 *
 * @description
 * This hook manages the login process, including form validation, API calls,
 * and storing authentication tokens. It also retrieves user data and active
 * branches after a successful login.
 *
 * @returns {Object} Hook state and functions.
 * @returns {boolean} loading - Indicates if the login request is in progress.
 * @returns {string} alertText - Error or informational messages.
 * @returns {boolean} showAlert - Controls visibility of the alert message.
 * @returns {number} wrongAttempts - Tracks incorrect login attempts.
 * @returns {Function} onLogin - Function to trigger the login process.
 * @returns {Function} setLoading - Allows external modification of loading state.
 * @returns {Function} setAlertText - Allows external modification of alert text.
 * @returns {Function} setShowAlert - Allows external control of the alert visibility.
 * @returns {Function} setWrongAttempts - Allows tracking of incorrect login attempts.
 *
 * @example
 * const { onLogin, loading, alertText, showAlert } = useOnLogin();
 *
 * async function handleLogin() {
 * }
 *
 * !UPDATES:
 * - Consider adding a return statement to the onLogin function to handles for success and errors.
 */
export function useOnLogin() {
  const [loading, setLoading] = useState(false);
  const [alertText, setAlertText] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [wrongAttempts, setWrongAttempts] = useState(0);

  async function onLogin(data, errorCallback) {
    let success = true;
    setAlertText("");
    setShowAlert(false);

    if (loading) return;

    if (!data.email) {
      setAlertText("Por favor informe o email.");
      setShowAlert(true);
      try {
        document.querySelector('[data-test="#auth-login-input-email"]').focus();
      } catch (error) { }
      return;
    }

    if (!data.password) {
      setAlertText("Por favor informe sua senha.");
      setShowAlert(true);
      try {
        document.querySelector('[data-test="#auth-login-input-password"]').focus();
      } catch (error) { }
      return;
    }

    setLoading(true);

    await login(data)
      .then(resp => {
        setCookieByName("access", resp.data.access);
        setCookieByName("refresh", resp.data.refresh);
        window.localStorage.setItem("easygestorweb_apikey", resp.data.access);
        window.localStorage.setItem("easygestorweb_refresh_token", resp.data.refresh);
      })
      .catch(error => {
        errorCallback();
        success = false;
        setLoading(false);

        if (error.response) {
          const { data } = error.response;
          if (data.email_not_found) {
            setAlertText("Falha no login. Email não encontrado.");
            setShowAlert(true);
            return;
          } else if (data.detail === "No active account found with the given credentials") {
            setAlertText("Falha no login. Email ou senha incorretos. Verifique os dados e tente novamente.");
            setShowAlert(true);
            return;
          }
        }
      });

    if (!success) return;

    await getUser()
      .then(user => {
        setCookieByName("username", user.name);
        window.localStorage.setItem("easygestorweb_userdata", JSON.stringify(user));
        setCookieByName("role", user.access_type);

        if (user.salesperson) {
          let branch = user.salesperson.branch_owner;
          if (branch.company) {
            branch.name = branch.company.fancy_name;
          }
          setCookieByName("branch_owner_name", branch.name);
          setCookieByName("branch_owner_id", branch.id);
          setCookieByName("branch_state_name", branch.address.state);
          window.localStorage.setItem("easygestorweb_current_branch", JSON.stringify(branch));
          window.location.href = "/";
        }
      });

    await getActiveBranch()
      .then(data => {
        const activeBranches = data.filter(b => b.status !== 2);


        if (!activeBranches.length) {
          window.location.href = "/branches/create";
          return;
        }

        let branch = activeBranches[0]

        setCookieByName("branch_owner_name", branch.name);
        setCookieByName("branch_count", activeBranches.length);
        setCookieByName("branch_owner_id", branch.id);
        setCookieByName("branch_state_name", branch.address.state);

        if (branch.branch_plan) {
          setCookieByName("branch_plan_id", branch.branch_plan.id);
        }

        window.localStorage.setItem("easygestorweb_current_branch", JSON.stringify(branch));
        window.location.href = "/";
      })
      .catch(() => {
        // Handle errors from getActiveBranch
      });

  }

  return {
    loading,
    alertText,
    showAlert,
    wrongAttempts,
    onLogin,
    setLoading,
    setAlertText,
    setShowAlert,
    setWrongAttempts,
  };
}

useOnLogin.propTypes = {
  /**
   * Function to trigger the login process.
   * @param {Object} data - The user login data containing email and password.
   * @param {Function} errorCallback - Callback function to handle login failure.
   */
  onLogin: PropTypes.func,
};

export default useOnLogin;



// WEBPACK FOOTER //
// ./src/views/Auth/Login/hooks/useOnLogin.js